/**
 * A feature, explained the first time it is touched.
 *
 * The loud half of a lesson — see `lessons.ts` for why there are two. This is
 * the only time the game explains a system unprompted, so it is allowed to
 * interrupt and allowed to take up room.
 *
 * ── It does not pause the game ──────────────────────────────────────────────
 *
 * Deliberately. The ship is still flying underneath, and the thing being
 * described is on screen behind the card — a jump module lesson with the
 * markers hidden would be teaching a diagram rather than the sky. It also means
 * a player who already knows can dismiss it and lose nothing.
 *
 * ── Down the right, not across the middle ───────────────────────────────────
 *
 * It was centred, on the argument that this is the one message per system that
 * should be read. True, and it does not follow: a lesson fires on an action
 * taken *while flying*, so the middle of the screen is the middle of the thing
 * the player is doing. Against the right edge it is still unmissable and no
 * longer sitting on the reticle.
 *
 * ── And it takes a real press to close ──────────────────────────────────────
 *
 * Any key used to do it. That is right for a card you asked for and wrong for
 * one that arrives unannounced mid-flight: the player is holding the controls,
 * so "any key" means the next thing they were going to do anyway, and the one
 * message per system that should be read was dismissed by a throttle input.
 *
 * So it is the button, and only the button. Escape is not a shortcut here
 * either — this is not a dialog somebody opened by mistake.
 */

import { useEffect, useState } from 'react';

import { HUD_BACKING, HUD_DIM, HUD_EDGE, HUD_ORANGE, HUD_TEXT } from './FlightHud';
import { keyLabel, type Lesson } from '@interspacial/shared';

import { HUD_SCALE, SHOW_KEY_HINTS } from './hudScale';

export interface LessonCardProps {
  lesson: Lesson;
  onDismiss: () => void;
  /** Turn the loud half off for good. The console keeps its one-liners. */
  onDisableTips?: () => void;

  /**
   * The cockpit's power, when there is a cockpit.
   *
   * A card is drawn by `App` rather than by the flight view, so it knew nothing
   * about the blackout and sat lit over a dead console through a whole crossing.
   * It comes from `useReportedPower` at the call site — see `SystemsPower`.
   */
  power?: React.CSSProperties | null;
}

export function LessonCard({ lesson, onDismiss, onDisableTips, power }: LessonCardProps) {
  /*
   * A short arming delay before the button will take a press.
   *
   * Lessons fire on an action the player just took — raising the module, first
   * contact with a rock — and the mouse may well be moving when the card
   * appears. Without this, a click already on its way lands on a button that was
   * not there when it was aimed, and the one chance to explain the system is
   * spent on nothing.
   */
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setArmed(true), 450);
    return () => window.clearTimeout(timer);
  }, [lesson.id]);

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        // Against the right edge, clear of the reticle and of the ship console
        // down the left. See the note at the top.
        justifyContent: 'flex-end',
        padding: '0 16px',
        // Not a backdrop over the whole screen: the thing being explained is
        // behind this, and dimming it would hide the lesson's own subject.
        pointerEvents: 'none',
        zIndex: 40,
      }}
    >
      <div
        style={{
          ...power,
          pointerEvents: 'auto',
          /*
           * Drawn at the HUD's scale, about the edge it is pinned to.
           *
           * `46vw` already shrinks the box on a narrow screen, which is not the
           * same thing: that gives a phone a card of the right width full of
           * desktop-sized text. The scale takes the type down with it, and the
           * origin keeps the card against the right edge as it goes rather than
           * letting it drift into the middle — which is the one place the note
           * at the top of this file says it must not be.
           *
           * The tube's collapse composes inside both, so a card caught by a jump
           * squashes out with the console instead of blinking off beside it.
           */
          transformOrigin: 'center right',
          ...(HUD_SCALE === 1 && !power
            ? {}
            : { transform: `scale(${HUD_SCALE}) ${power?.transform ?? ''}` }),
          // Narrower than it was, because it no longer has the width of the
          // screen to spend and a column of text is easier to read anyway.
          width: 'min(390px, 46vw)',
          background: HUD_BACKING,
          border: `1px solid ${HUD_ORANGE}`,
          boxShadow: '0 0 40px rgba(0,0,0,0.65)',
          color: HUD_TEXT,
          padding: '18px 22px 16px',
        }}
      >
        <div
          style={{
            fontSize: 10,
            letterSpacing: 2,
            textTransform: 'uppercase',
            color: HUD_DIM,
            marginBottom: 2,
          }}
        >
          ship systems
        </div>

        <div style={{ fontSize: 19, color: HUD_ORANGE, letterSpacing: 1, marginBottom: 10 }}>
          {lesson.title}
        </div>

        {lesson.lines.map((line, index) => (
          <p
            key={index}
            style={{
              margin: '0 0 9px',
              fontSize: 13,
              lineHeight: 1.55,
            }}
          >
            {line}
          </p>
        ))}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginTop: 12,
            borderTop: `1px solid ${HUD_EDGE}`,
            paddingTop: 10,
          }}
        >
          {/*
              How to reach the button, which is not obvious and is not optional.
              The cockpit holds pointer lock while you fly, so there is no cursor
              to click with until you ask for one — and a card that can only be
              closed by a click nobody can make is worse than one that closes too
              easily.

              It doubles as the place that teaches the mechanic. Reaching a
              control without letting go of the stick is something the player
              needs for the radio and every switch on the HUD, and this is the
              first time the game has a reason to mention it.
          */}
          <span style={{ flex: 1, fontSize: 10.5, color: HUD_DIM, lineHeight: 1.35 }}>
            {!armed ? ' ' : SHOW_KEY_HINTS ? `hold [${keyLabel('cursor')}] for a cursor` : ' '}
          </span>

          {/* The switch, offered where the thing it switches off is happening.
              Buried in a settings menu it would be found by nobody who wanted
              it — and the people who want it are, by definition, reading this. */}
          {onDisableTips && (
            <button type="button" onClick={onDisableTips} style={QUIET}>
              turn these off
            </button>
          )}

          <button type="button" onClick={onDismiss} style={ACTION} disabled={!armed}>
            got it
          </button>
        </div>
      </div>
    </div>
  );
}

const ACTION: React.CSSProperties = {
  background: 'rgba(255,138,61,0.16)',
  border: `1px solid ${HUD_ORANGE}`,
  color: HUD_ORANGE,
  font: 'inherit',
  fontSize: 12,
  padding: '5px 14px',
  cursor: 'pointer',
};

const QUIET: React.CSSProperties = {
  background: 'transparent',
  border: `1px solid ${HUD_EDGE}`,
  color: HUD_DIM,
  font: 'inherit',
  fontSize: 10.5,
  padding: '5px 10px',
  cursor: 'pointer',
};
